import React, { useState } from 'react';
import PropTypes from 'prop-types';

import useDispatch from 'client/hooks/useDispatch';
import requestUtilityApi from 'client/actions/requestUtilityApi';
import { SR_API_ACTION_UTILITY_ACTIVATION_TRACK_INTERNET_FLOW } from 'api/utilityActionTypes';

import {
  ANALYTICS_EVENT_UTILITY_SETUP_INTERNET_FLOW_BACK_TO_INITIAL_STATE,
  ANALYTICS_EVENT_UTILITY_SETUP_INTERNET_FLOW_CURRENT_PLAN_DETAILS,
  ANALYTICS_EVENT_UTILITY_SETUP_INTERNET_FLOW_PROVIDER_NOT_AVAILABLE,
  ANALYTICS_EVENT_UTILITY_SETUP_INTERNET_FLOW_SEARCH_PROVIDER_NAME,
} from 'client/analytics/eventList';

import SrUtilitySetupTransferInternetFlowCurrentProviderNameModal from 'client/components/SrUtilitySetup/SrUtilityTransferInternetFlow/SrUtilitySetupTransferInternetFlowCurrentProviderNameModal';
import SrUtilitySetupTransferInternetFlowProviderNotAvailableModal from 'client/components/SrUtilitySetup/SrUtilityTransferInternetFlow/SrUtilitySetupTransferInternetFlowProviderNotAvailableModal';
import SrUtilitySetupTransferInternetFlowCurrentPlanDetailsModal from 'client/components/SrUtilitySetup/SrUtilityTransferInternetFlow/SrUtilitySetupTransferInternetFlowCurrentPlanDetailsModal';
import SrUtilitySetupTransferInternetFlowReplaceModem from 'client/components/SrUtilitySetup/SrUtilityTransferInternetFlow/SrUtilitySetupTransferInternetFlowReplaceModem';

const INTERNET_TRANSFER_FLOW_STEP_CURRENT_PROVIDER_NAME = 'CURRENT_PROVIDER_NAME';
const INTERNET_TRANSFER_FLOW_STEP_PROVIDER_NOT_AVAILABLE = 'PROVIDER_NOT_AVAILABLE';
const INTERNET_TRANSFER_FLOW_STEP_PLAN_DETAILS = 'PLAN_DETAILS';
const INTERNET_TRANSFER_FLOW_STEP_REPLACE_MODEM = 'REPLACE_MODEM';

const SrUtilitySetupTransferInternetFlow = ({
  utilityActivationId,
  availableProviders = [],
  onClose,
  onSeeDeals,
}) => {
  const [currentStep, setCurrentStep] = useState(INTERNET_TRANSFER_FLOW_STEP_CURRENT_PROVIDER_NAME);
  const [selectedProvider, setSelectedProvider] = useState(null);

  const dispatch = useDispatch();

  const trackInternetFlowEvent = (internetFlowEventName) => {
    try {
      return dispatch(requestUtilityApi, {
        action: SR_API_ACTION_UTILITY_ACTIVATION_TRACK_INTERNET_FLOW,
        payload: {
          utilityActivationId,
          internetFlowEventName,
        },
      });
    } catch (error) {} // eslint-disable-line no-empty
  };

  const handleOnClose = () => {
    trackInternetFlowEvent(ANALYTICS_EVENT_UTILITY_SETUP_INTERNET_FLOW_BACK_TO_INITIAL_STATE);
    return onClose();
  };

  const handleProviderSearch = ({ providerId, providerName }) => {
    if (!providerId) {
      trackInternetFlowEvent(ANALYTICS_EVENT_UTILITY_SETUP_INTERNET_FLOW_PROVIDER_NOT_AVAILABLE);
      setCurrentStep(INTERNET_TRANSFER_FLOW_STEP_PROVIDER_NOT_AVAILABLE);
    }

    const availableProvider = availableProviders.find(({ id }) => id === providerId);

    if (!availableProvider) {
      setSelectedProvider({ title: providerName });
      trackInternetFlowEvent(ANALYTICS_EVENT_UTILITY_SETUP_INTERNET_FLOW_PROVIDER_NOT_AVAILABLE);
      return setCurrentStep(INTERNET_TRANSFER_FLOW_STEP_PROVIDER_NOT_AVAILABLE);
    }

    trackInternetFlowEvent(ANALYTICS_EVENT_UTILITY_SETUP_INTERNET_FLOW_CURRENT_PLAN_DETAILS);
    setSelectedProvider(availableProvider);
    return setCurrentStep(INTERNET_TRANSFER_FLOW_STEP_PLAN_DETAILS);
  };

  const handleOnCurrentPlanDetailsSubmit = () => {
    return setCurrentStep(INTERNET_TRANSFER_FLOW_STEP_REPLACE_MODEM);
  };

  const handleSeeDeals = () => {
    return onSeeDeals(selectedProvider.id);
  };

  const handleOnCurrentPlanDetailsBack = () => {
    trackInternetFlowEvent(ANALYTICS_EVENT_UTILITY_SETUP_INTERNET_FLOW_CURRENT_PLAN_DETAILS);
    return setCurrentStep(INTERNET_TRANSFER_FLOW_STEP_CURRENT_PROVIDER_NAME);
  };

  const handleOnBackProviderNotAvailable = () => {
    trackInternetFlowEvent(ANALYTICS_EVENT_UTILITY_SETUP_INTERNET_FLOW_SEARCH_PROVIDER_NAME);
    return setCurrentStep(INTERNET_TRANSFER_FLOW_STEP_CURRENT_PROVIDER_NAME);
  };

  switch (currentStep) {
    case INTERNET_TRANSFER_FLOW_STEP_CURRENT_PROVIDER_NAME:
      return (
        <SrUtilitySetupTransferInternetFlowCurrentProviderNameModal
          onSubmit={handleProviderSearch}
          onBack={handleOnClose}
        />
      );
    case INTERNET_TRANSFER_FLOW_STEP_PROVIDER_NOT_AVAILABLE:
      return (
        <SrUtilitySetupTransferInternetFlowProviderNotAvailableModal
          providerName={selectedProvider?.title}
          onSubmit={handleOnClose}
          onBack={handleOnBackProviderNotAvailable}
        />
      );
    case INTERNET_TRANSFER_FLOW_STEP_PLAN_DETAILS:
      return (
        <SrUtilitySetupTransferInternetFlowCurrentPlanDetailsModal
          providerName={selectedProvider?.title}
          onSubmit={handleOnCurrentPlanDetailsSubmit}
          onBack={handleOnCurrentPlanDetailsBack}
        />
      );
    case INTERNET_TRANSFER_FLOW_STEP_REPLACE_MODEM:
      return (
        <SrUtilitySetupTransferInternetFlowReplaceModem
          providerName={selectedProvider?.title}
          onSubmit={handleSeeDeals}
          onBack={handleOnCurrentPlanDetailsBack}
        />
      );
    default:
      return null;
  }
};

SrUtilitySetupTransferInternetFlow.propTypes = {
  utilityActivationId: PropTypes.number.isRequired,
  availableProviders: PropTypes.array.isRequired,
  onSeeDeals: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

SrUtilitySetupTransferInternetFlow.displayName = 'SrUtilitySetupTransferInternetFlow';

export default SrUtilitySetupTransferInternetFlow;
